import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import ImageBlock from "../components/ImageBlock";
import { Reveal } from "../components/Reveal";
import { SPORTS_CATEGORIES, SPORTS_DISCLAIMER, SPORTS_SUPPORT } from "../data/sports";
import { MEDIA } from "../data/media";
import { BASE_KEYWORDS, breadcrumbSchema, PAGE_KEYWORDS } from "../data/brand";

const EXPERIENCE_POINTS = [
  "Two big screens keep you connected to the action",
  "Grab a drink, order your Tacoverse favorites and catch the game",
  "Meet the crew — watch parties and game days welcome",
];

export default function Sports() {
  return (
    <div className="pt-28 md:pt-36" data-testid="sports-page">
      <Seo
        title="Watch Sports on Two Big Screens — NFL, NBA, MLB & More"
        description="Catch the game on two big screens at Barverse — NFL, NBA, MLB, NHL, college sports, soccer and major events, with drinks and Tacoverse food."
        path="/sports"
        jsonLd={breadcrumbSchema("/sports", "Sports")}
        keywords={`${BASE_KEYWORDS}, ${PAGE_KEYWORDS.sports}`}
      />

      <header className="container-x">
        <Reveal>
          <p className="eyebrow">Sports at Barverse</p>
          <h1 className="mt-5 font-display text-6xl uppercase leading-[0.9] text-bv-ink sm:text-7xl lg:text-8xl">
            Game on.
          </h1>
          <p className="mt-6 font-mono text-xs font-bold uppercase tracking-[0.22em] text-bv-flame md:text-sm">
            {SPORTS_SUPPORT}
          </p>
        </Reveal>
      </header>

      <div className="container-x mt-12">
        <Reveal>
          <ImageBlock src={MEDIA.sports.src} alt={MEDIA.sports.alt} className="aspect-[16/9] w-full md:aspect-[21/9]" eager />
        </Reveal>
      </div>

      <section className="container-x mt-20 pb-8" aria-label="Sports categories">
        <Reveal>
          <h2 className="font-display text-2xl uppercase text-bv-ink md:text-3xl">
            What you'll find on
          </h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {SPORTS_CATEGORIES.map((s, i) => (
            <Reveal key={s.abbr} delay={i * 0.04}>
              <div
                className="group h-full rounded-2xl border border-bv-line bg-bv-panel p-6 transition-colors duration-200 hover:border-bv-flame/60"
                data-testid={`sports-category-${s.abbr.toLowerCase()}`}
              >
                <p className="font-mono text-[10px] font-bold text-bv-faint">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-3 font-display text-2xl uppercase text-bv-ink sm:text-3xl">
                  {s.name}
                </p>
                <span className="mt-4 block h-0.5 w-8 bg-bv-flame/60 transition-all duration-300 group-hover:w-16" aria-hidden="true" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="container-x mt-16 pb-24">
        <div className="grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-2xl uppercase text-bv-ink md:text-3xl">
              Catch the game at Barverse
            </h2>
            <ul className="mt-6 space-y-4">
              {EXPERIENCE_POINTS.map((p, i) => (
                <li key={p} className="flex items-start gap-4 border-b border-bv-line/70 pb-4">
                  <span className="font-mono text-[10px] font-bold text-bv-faint">{String(i + 1).padStart(2, "0")}</span>
                  <span className="leading-relaxed text-bv-sub">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-5">
            <div className="h-full rounded-2xl border border-bv-line bg-bv-panel p-8" data-testid="sports-disclaimer">
              <p className="field-label">Programming note</p>
              <p className="text-sm leading-relaxed text-bv-sub">{SPORTS_DISCLAIMER}</p>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-12">
          <div className="flex flex-wrap gap-4">
            <Link to="/locations" className="btn-primary" data-testid="sports-cta-find">Find a Barverse</Link>
            <Link to="/groups" className="btn-ghost" data-testid="sports-cta-groups">Bring Your Crew</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
